import React, { useEffect, useState } from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import { X, UserPlus, Users, ArrowRight, Loader2, Globe, Shield, Lock } from 'lucide-react';

const TeamActionModal: React.FC = () => {
    const { isTeamModalOpen, closeTeamModal, createTeam, joinTeam } = useStandUp();
    const [mode, setMode] = useState<'menu' | 'create' | 'join'>('menu');
    const [inputValue, setInputValue] = useState('');
    const [visibility, setVisibility] = useState<'public' | 'protected' | 'private'>('public');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isInputTouched, setIsInputTouched] = useState(false);

    const isInputEmpty = !inputValue.trim();
    const isSubmitDisabled = loading || isInputEmpty;
    const requiredFieldLabel = mode === 'create' ? 'Team Name' : 'Team ID';

    // Handle Escape Key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                closeTeamModal();
            }
        };
        if (isTeamModalOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isTeamModalOpen]);

    if (!isTeamModalOpen) return null;

    const handleClose = () => {
        setMode('menu');
        setInputValue('');
        setVisibility('public');
        setError('');
        setIsInputTouched(false);
        closeTeamModal();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsInputTouched(true);
        if (!inputValue.trim()) {
            setError(`${requiredFieldLabel} is required.`);
            return;
        }

        setLoading(true);
        setError('');

        try {
            if (mode === 'create') {
                await createTeam(inputValue, visibility);
            } else {
                await joinTeam(inputValue);
            }
            handleClose();
        } catch (err: any) {
            console.error(err);
            setError(err.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50 shrink-0">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                        {mode === 'menu' ? 'Teams' : mode === 'create' ? 'Create Team' : 'Join Team'}
                    </h2>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar">
                    {mode === 'menu' ? (
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => setMode('create')}
                                className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95"
                            >
                                <UserPlus size={20} /> Create New Team
                            </button>
                            <button
                                onClick={() => setMode('join')}
                                className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                <Users size={20} /> Join with ID
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 px-1">
                                    {mode === 'create' ? 'Team Name' : 'Team ID'} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    required
                                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-base focus:ring-2 ring-brand-500 outline-none dark:text-white transition-all"
                                    placeholder={mode === 'create' ? "e.g. Design Squad" : "Paste Team ID here..."}
                                    value={inputValue}
                                    onBlur={() => setIsInputTouched(true)}
                                    onChange={e => {
                                        const nextValue = e.target.value;
                                        setInputValue(nextValue);
                                        if (nextValue.trim()) {
                                            setError('');
                                        }
                                    }}
                                />
                                {isInputTouched && isInputEmpty && (
                                    <p className="text-red-500 text-xs mt-2 font-medium">{requiredFieldLabel} is required.</p>
                                )}
                                {error && <p className="text-red-500 text-xs mt-2 font-medium">{error}</p>}
                            </div>

                            {mode === 'create' && (
                                <div className="space-y-3">
                                    <label className="block text-xs font-bold text-gray-500 uppercase px-1">Privacy & Access</label>

                                    {/* Public Option */}
                                    <button
                                        type="button"
                                        onClick={() => setVisibility('public')}
                                        className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${visibility === 'public' ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-500 ring-1 ring-brand-500' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-300'}`}
                                    >
                                        <div className={`mt-0.5 p-1.5 rounded-lg ${visibility === 'public' ? 'bg-brand-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                            <Globe size={16} />
                                        </div>
                                        <div className="flex-1">
                                            <p className={`text-sm font-bold ${visibility === 'public' ? 'text-brand-900 dark:text-brand-200' : 'text-gray-900 dark:text-white'}`}>Public</p>
                                            <p className="text-[10px] text-gray-500 leading-tight mt-0.5">Anyone in the organization can search and join instantly.</p>
                                        </div>
                                    </button>

                                    {/* Protected Option */}
                                    <button
                                        type="button"
                                        onClick={() => setVisibility('protected')}
                                        className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${visibility === 'protected' ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-500 ring-1 ring-brand-500' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-300'}`}
                                    >
                                        <div className={`mt-0.5 p-1.5 rounded-lg ${visibility === 'protected' ? 'bg-brand-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                            <Shield size={16} />
                                        </div>
                                        <div className="flex-1">
                                            <p className={`text-sm font-bold ${visibility === 'protected' ? 'text-brand-900 dark:text-brand-200' : 'text-gray-900 dark:text-white'}`}>Protected</p>
                                            <p className="text-[10px] text-gray-500 leading-tight mt-0.5">Visible in search, but requires manager approval to join.</p>
                                        </div>
                                    </button>

                                    {/* Private Option */}
                                    <button
                                        type="button"
                                        onClick={() => setVisibility('private')}
                                        className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${visibility === 'private' ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-500 ring-1 ring-brand-500' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-300'}`}
                                    >
                                        <div className={`mt-0.5 p-1.5 rounded-lg ${visibility === 'private' ? 'bg-brand-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                            <Lock size={16} />
                                        </div>
                                        <div className="flex-1">
                                            <p className={`text-sm font-bold ${visibility === 'private' ? 'text-brand-900 dark:text-brand-200' : 'text-gray-900 dark:text-white'}`}>Private</p>
                                            <p className="text-[10px] text-gray-500 leading-tight mt-0.5">Hidden from search. Only people you invite can join.</p>
                                        </div>
                                    </button>
                                </div>
                            )}

                            <div className="flex gap-3 pt-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => { setMode('menu'); setError(''); setIsInputTouched(false); }}
                                    className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold py-2.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitDisabled}
                                    className={`flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:text-white disabled:opacity-100 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-lg shadow-sm flex items-center justify-center gap-2 ${isSubmitDisabled ? 'transition-colors' : 'transition-transform active:scale-95'}`}
                                >
                                    {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                                    {mode === 'create' ? 'Create' : 'Join'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamActionModal;
